import { useEffect, useState } from 'react';

import { DeleteOutlined, InboxOutlined, PlusOutlined } from '@ant-design/icons';
import {
  Alert,
  App,
  Button,
  Col,
  Divider,
  Form,
  Input,
  InputNumber,
  Modal,
  Radio,
  Row,
  Segmented,
  Select,
  Skeleton,
  Space,
  Steps,
  Tabs,
  Tooltip,
  Upload,
  theme,
} from 'antd';
import { UploadFile } from 'antd/es/upload/interface';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

import { ApiErrorData } from 'models/ApiResponse';
import {
  CreateQuizInput,
  CreateQuizQuestionInput,
  QuizContentType,
  QuizContentTypeOption,
  QuizDetail,
  QuizTypeOption,
  UpdateQuizInput,
  VideoUploadUrlResponse,
} from 'models/Quiz';

import { QuizService } from 'services/QuizService';

import appI18n from '../../../config/i18n';

const { Dragger } = Upload;

const COUNTRY_OPTIONS = [{ value: 'CL' }, { value: 'PE' }, { value: 'CO' }];
const DEFAULT_COUNTRY = 'CL';
const DEFAULT_QUIZ_TYPE = 'INDUCTION_FLOW';

const STEP_GENERAL = 0;
const STEP_CONTENT = 1;
const STEP_QUESTIONS = 2;
const LAST_STEP = STEP_QUESTIONS;

interface OptionFormValue {
  text: string;
}

interface QuestionFormValue {
  text: string;
  // Se conserva para no perder el estado al editar, pero ya no se expone en la
  // UI (las preguntas nuevas nacen activas).
  active: boolean;
  correctIndex: number;
  options: OptionFormValue[];
}

interface FormValues {
  countryCode: string;
  quizType: string;
  name: string;
  description?: string;
  questionsToShow: number;
  passingScore: number;
  contentType: QuizContentType;
  contentUrl?: string;
  questions: QuestionFormValue[];
}

interface Props {
  isOpen: boolean;
  mode?: 'create' | 'edit';
  quiz?: QuizDetail | null;
  loading?: boolean;
  defaultCountry?: string;
  quizTypes: QuizTypeOption[];
  contentTypes: QuizContentTypeOption[];
  maxVideoSizeMb?: number;
  handleModalCancel: () => void;
  onSaved: () => void;
}

// Mapea el campo con error al paso del wizard que lo contiene, para poder
// saltar al paso correcto cuando la validación final falla en un paso oculto.
const fieldStep = (fieldName?: string): number => {
  if (fieldName === 'contentType' || fieldName === 'contentUrl') {
    return STEP_CONTENT;
  }
  if (fieldName === 'questions') return STEP_QUESTIONS;
  return STEP_GENERAL;
};

function CreateQuizModal({
  isOpen,
  mode = 'create',
  quiz,
  loading = false,
  defaultCountry,
  quizTypes,
  contentTypes,
  maxVideoSizeMb,
  handleModalCancel,
  onSaved,
}: Props) {
  const isEditMode = mode === 'edit';
  const { t } = useTranslation(undefined, { i18n: appI18n });
  const { message } = App.useApp();
  const { token } = theme.useToken();
  const [form] = Form.useForm<FormValues>();

  const [current, setCurrent] = useState<number>(STEP_GENERAL);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [questionsTouched, setQuestionsTouched] = useState<boolean>(false);

  // Estado del flujo R2 (touch_storage)
  const [r2Key, setR2Key] = useState<string | null>(null);
  const [uploading, setUploading] = useState<boolean>(false);
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);
  const [uploadedFileUrl, setUploadedFileUrl] = useState<string | null>(null);
  const [uploadPercent, setUploadPercent] = useState<number>(0);

  const contentType = Form.useWatch('contentType', form);

  // Reactivo: cantidad de preguntas en el banco vs. preguntas a mostrar.
  // Permite avisar en vivo (paso 3) si el banco quedó corto al cambiar el
  // valor de "preguntas a mostrar" en el paso 1.
  const watchedQuestions = Form.useWatch('questions', form);
  const watchedToShow = Form.useWatch('questionsToShow', form);
  const questionCount = Array.isArray(watchedQuestions)
    ? watchedQuestions.length
    : 0;
  const missingQuestions = Math.max((watchedToShow || 0) - questionCount, 0);

  // Apertura del modal: reset de estado y, en creación, valores por defecto.
  useEffect(() => {
    if (!isOpen) return;
    form.resetFields();
    setCurrent(STEP_GENERAL);
    setQuestionsTouched(false);
    setR2Key(null);
    setUploadedFileName(null);
    setUploadedFileUrl(null);
    setUploadPercent(0);

    if (mode === 'create') {
      form.setFieldsValue({
        countryCode: defaultCountry || DEFAULT_COUNTRY,
        quizType: DEFAULT_QUIZ_TYPE,
        contentType: 'youtube',
        questionsToShow: 1,
        passingScore: 1,
        questions: [
          {
            text: '',
            active: true,
            correctIndex: 0,
            options: [{ text: '' }, { text: '' }],
          },
        ],
      });
    }
  }, [isOpen]);

  // Edición: poblar el formulario cuando llega el detalle (puede tardar; se
  // muestra un skeleton mientras tanto).
  useEffect(() => {
    if (!isOpen || mode !== 'edit' || !quiz) return;
    form.setFieldsValue({
      countryCode: quiz.countryCode,
      quizType: quiz.quizType,
      name: quiz.name,
      description: quiz.description || undefined,
      questionsToShow: quiz.questionsToShow,
      passingScore: quiz.passingScore,
      contentType: quiz.contentType,
      contentUrl: quiz.contentUrl || undefined,
      questions: quiz.questions
        .slice()
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map((question) => {
          const options = question.options
            .slice()
            .sort((a, b) => a.sortOrder - b.sortOrder);
          return {
            text: question.text,
            active: question.active,
            correctIndex: Math.max(
              options.findIndex((o) => o.isCorrect),
              0
            ),
            options: options.map((o) => ({ text: o.text })),
          };
        }),
    });
    if (quiz.contentType === 'touch_storage') {
      setR2Key(quiz.r2Key);
    }
  }, [isOpen, quiz]);

  const countryOptions = COUNTRY_OPTIONS.map((opt) => {
    const code = opt.value.toLowerCase();
    return {
      value: opt.value,
      label: (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <img
            src={`https://flagcdn.com/16x12/${code}.png`}
            srcSet={`https://flagcdn.com/32x24/${code}.png 2x`}
            width={16}
            height={12}
            alt=""
            style={{ display: 'block' }}
          />
          <span>
            {t(`scenes.quizzes.scene.countries.${opt.value}`, opt.value)}
          </span>
        </span>
      ),
    };
  });

  const quizTypeOptions = quizTypes.map((item) => ({
    value: item.code,
    label: item.label,
  }));

  const contentTypeSegments = contentTypes.map((item) => ({
    value: item.code,
    label: item.label,
  }));

  /**
   * Flujo R2 de 3 pasos para touch_storage:
   * 1. Pedir presigned PUT URL al BFF.
   * 2. PUT directo al bucket con axios LIMPIO (sin interceptores que rompan
   *    la firma de R2).
   * 3. Guardar el r2Key para enviarlo en el submit del quiz.
   * Devuelve false para evitar el upload automático de antd.
   */
  const beforeUpload = async (file: File): Promise<boolean> => {
    if (file.type !== 'video/mp4') {
      message.error(t('scenes.quizzes.scene.create.errors.video_type'), 3.5);
      return false;
    }

    const fileSizeMb = file.size / 1024 / 1024;
    if (maxVideoSizeMb && fileSizeMb > maxVideoSizeMb) {
      message.error(
        t('scenes.quizzes.scene.create.errors.video_too_large', {
          max: maxVideoSizeMb,
        }),
        3.5
      );
      return false;
    }

    setUploading(true);
    setUploadPercent(0);
    setR2Key(null);
    setUploadedFileName(file.name);
    setUploadedFileUrl(null);

    // Paso 1: presigned URL
    const up = await QuizService.getVideoUploadUrl({
      mimeType: 'video/mp4',
      fileSizeMb: Math.ceil(fileSizeMb),
    });
    if (!up.success) {
      const error = up.data as ApiErrorData;
      message.error(
        (error.message as string) ||
          t('scenes.quizzes.scene.create.errors.upload_url_failed'),
        3.5
      );
      setUploading(false);
      setUploadedFileName(null);
      return false;
    }

    const { uploadUrl, r2Key: newR2Key } = up.data as VideoUploadUrlResponse;

    // Paso 2: PUT directo a R2 con axios limpio (sin MainBackendAxios).
    // onUploadProgress alimenta la barra de progreso del item del Upload.
    try {
      await axios.put(uploadUrl, file, {
        headers: { 'Content-Type': 'video/mp4' },
        onUploadProgress: (e) => {
          if (e.total) {
            setUploadPercent(Math.round((e.loaded / e.total) * 100));
          }
        },
      });
    } catch (e) {
      message.error(t('scenes.quizzes.scene.create.errors.upload_failed'), 3.5);
      setUploading(false);
      setUploadedFileName(null);
      return false;
    }

    // Paso 3: guardar r2Key y un blob URL local para poder previsualizar
    // el video recién subido (abrir en otra pestaña), igual que el de la BD.
    setR2Key(newR2Key);
    setUploadedFileUrl(URL.createObjectURL(file));
    setUploadPercent(100);
    setUploading(false);
    message.success(t('scenes.quizzes.scene.create.video_uploaded'), 2.5);
    return false;
  };

  const buildQuestionsPayload = (
    questions: QuestionFormValue[]
  ): CreateQuizQuestionInput[] =>
    questions.map((question, qIdx) => ({
      text: question.text.trim(),
      sortOrder: qIdx,
      active: question.active ?? true,
      options: question.options.map((option, oIdx) => ({
        text: option.text.trim(),
        isCorrect: oIdx === question.correctIndex,
        sortOrder: oIdx,
      })),
    }));

  // Reglas de negocio del banco de preguntas (paso 3). Devuelve el mensaje de
  // error o null si todo está bien.
  const validateQuestionBank = (values: FormValues): string | null => {
    if (!values.questions || values.questions.length < 1) {
      return t('scenes.quizzes.scene.create.validations.min_questions');
    }
    for (const question of values.questions) {
      if (!question.options || question.options.length < 2) {
        return t('scenes.quizzes.scene.create.validations.min_options');
      }
      if (
        question.correctIndex === undefined ||
        question.correctIndex < 0 ||
        question.correctIndex >= question.options.length
      ) {
        return t('scenes.quizzes.scene.create.validations.one_correct');
      }
    }
    // El banco debe tener al menos tantas preguntas como las que se muestran.
    if (values.questions.length < values.questionsToShow) {
      return t(
        'scenes.quizzes.scene.create.validations.questions_insufficient',
        {
          toShow: values.questionsToShow,
          count: values.questions.length,
          missing: values.questionsToShow - values.questions.length,
        }
      );
    }
    return null;
  };

  // Validación específica del paso de contenido (no son reglas de antd Form).
  const validateContentStep = async (): Promise<boolean> => {
    try {
      await form.validateFields(['contentType']);
    } catch (e) {
      return false;
    }
    const ct = form.getFieldValue('contentType');
    if (ct === 'touch_storage') {
      if (!r2Key) {
        message.error(
          t('scenes.quizzes.scene.create.validations.video_required'),
          3.5
        );
        return false;
      }
    } else {
      try {
        await form.validateFields(['contentUrl']);
      } catch (e) {
        return false;
      }
    }
    return true;
  };

  const goNext = async () => {
    if (current === STEP_GENERAL) {
      try {
        await form.validateFields([
          'countryCode',
          'quizType',
          'name',
          'questionsToShow',
          'passingScore',
        ]);
      } catch (e) {
        return;
      }
    } else if (current === STEP_CONTENT) {
      const ok = await validateContentStep();
      if (!ok) return;
    }
    setCurrent((c) => Math.min(c + 1, LAST_STEP));
  };

  const goPrev = () => setCurrent((c) => Math.max(c - 1, STEP_GENERAL));

  const handleSubmit = async () => {
    let values: FormValues;
    try {
      values = await form.validateFields();
    } catch (errInfo: any) {
      // Si el error está en un paso oculto, saltamos a ese paso.
      const firstField = errInfo?.errorFields?.[0]?.name?.[0] as
        | string
        | undefined;
      setCurrent(fieldStep(firstField));
      return;
    }

    // Contenido: validación que no cubren las reglas de antd Form.
    const contentOk = await validateContentStep();
    if (!contentOk) {
      setCurrent(STEP_CONTENT);
      return;
    }

    const bankError = validateQuestionBank(values);
    if (bankError) {
      setCurrent(STEP_QUESTIONS);
      message.error(bankError, 3.5);
      return;
    }

    if (isEditMode && quiz) {
      await handleUpdate(values);
    } else {
      await handleCreate(values);
    }
  };

  const handleCreate = async (values: FormValues) => {
    const description = values.description?.trim();

    const body: CreateQuizInput = {
      countryCode: values.countryCode,
      name: values.name.trim(),
      quizType: values.quizType,
      contentType: values.contentType,
      questionsToShow: values.questionsToShow,
      passingScore: values.passingScore,
      questions: buildQuestionsPayload(values.questions),
    };
    if (description) body.description = description;
    if (values.contentType === 'touch_storage') {
      body.r2Key = r2Key as string;
    } else {
      body.contentUrl = values.contentUrl?.trim();
    }

    setSubmitting(true);
    const response = await QuizService.createQuiz(body);
    setSubmitting(false);

    if (response.success) {
      handleModalCancel();
      onSaved();
    } else {
      const error = response.data as ApiErrorData;
      message.error(
        (error.message as string) ||
          t('scenes.quizzes.scene.create.errors.failed'),
        3.5
      );
    }
  };

  const handleUpdate = async (values: FormValues) => {
    if (!quiz) return;

    // Update FROZEN-SAFE: armamos el body sólo con los campos que cambiaron
    // respecto al quiz original (diff). Las questions se incluyen únicamente
    // si el usuario tocó el banco (questionsTouched).
    const body: UpdateQuizInput = {};

    const newName = values.name.trim();
    if (newName !== quiz.name) body.name = newName;

    const newDescription = values.description?.trim() || null;
    const oldDescription = quiz.description || null;
    if (newDescription !== oldDescription) body.description = newDescription;

    if (values.quizType !== quiz.quizType) body.quizType = values.quizType;

    if (values.questionsToShow !== quiz.questionsToShow) {
      body.questionsToShow = values.questionsToShow;
    }
    if (values.passingScore !== quiz.passingScore) {
      body.passingScore = values.passingScore;
    }

    // Contenido de video
    if (values.contentType !== quiz.contentType) {
      body.contentType = values.contentType;
    }
    if (values.contentType === 'touch_storage') {
      if (r2Key && r2Key !== quiz.r2Key) body.r2Key = r2Key;
    } else {
      const newUrl = values.contentUrl?.trim() || null;
      const oldUrl = quiz.contentUrl || null;
      if (newUrl !== oldUrl) body.contentUrl = newUrl;
    }

    if (questionsTouched) {
      body.questions = buildQuestionsPayload(values.questions);
    }

    setSubmitting(true);
    const response = await QuizService.updateQuiz(quiz.id, body);
    setSubmitting(false);

    if (response.success) {
      message.success(t('scenes.quizzes.scene.create.edit_success'), 3);
      handleModalCancel();
      onSaved();
    } else {
      const error = response.data as ApiErrorData;
      const code = String(error.code || '');
      const errMsg = String(error.message || '');
      if (code.includes('frozen') || errMsg.toLowerCase().includes('frozen')) {
        message.error(t('scenes.quizzes.scene.create.errors.frozen'), 4.5);
      } else {
        message.error(
          (error.message as string) ||
            t('scenes.quizzes.scene.create.errors.update_failed'),
          3.5
        );
      }
    }
  };

  const modalTitle = isEditMode
    ? t('scenes.quizzes.scene.create.edit_title')
    : t('scenes.quizzes.scene.create.title');
  const finalOkText = isEditMode
    ? t('scenes.quizzes.scene.create.buttons.save')
    : t('scenes.quizzes.scene.create.buttons.create');

  // Lista de archivos del Dragger:
  // - subiendo: item con barra de progreso (status uploading + percent).
  // - recién subido: item clickeable que abre el blob local en otra pestaña.
  // - edición de un quiz touch_storage existente: item con el video presigned.
  const buildUploadFileList = (): UploadFile[] => {
    if (uploading) {
      return [
        {
          uid: 'uploading',
          name: uploadedFileName || 'video.mp4',
          status: 'uploading',
          percent: uploadPercent,
        },
      ];
    }
    if (uploadedFileName && uploadedFileUrl) {
      return [
        {
          uid: '-1',
          name: uploadedFileName,
          status: 'done',
          url: uploadedFileUrl,
        },
      ];
    }
    if (
      isEditMode &&
      quiz?.contentType === 'touch_storage' &&
      r2Key &&
      quiz.videoUrl
    ) {
      return [
        {
          uid: 'current',
          name: t('scenes.quizzes.scene.create.current_video'),
          status: 'done',
          url: quiz.videoUrl,
        },
      ];
    }
    return [];
  };
  const uploadFileList: UploadFile[] = buildUploadFileList();

  // Edición: tabs + un único botón Guardar. Creación: wizard con Atrás/Siguiente.
  const footer = isEditMode ? (
    <Space>
      <Button onClick={handleModalCancel}>
        {t('scenes.quizzes.scene.create.buttons.cancel')}
      </Button>
      <Button
        type="primary"
        loading={submitting}
        disabled={loading || !quiz}
        onClick={handleSubmit}
      >
        {finalOkText}
      </Button>
    </Space>
  ) : (
    <Space>
      <Button onClick={handleModalCancel}>
        {t('scenes.quizzes.scene.create.buttons.cancel')}
      </Button>
      {current > STEP_GENERAL && (
        <Button onClick={goPrev}>
          {t('scenes.quizzes.scene.create.buttons.back')}
        </Button>
      )}
      {current < LAST_STEP && (
        <Button type="primary" onClick={goNext}>
          {t('scenes.quizzes.scene.create.buttons.next')}
        </Button>
      )}
      {current === LAST_STEP && (
        <Button type="primary" loading={submitting} onClick={handleSubmit}>
          {finalOkText}
        </Button>
      )}
    </Space>
  );

  return (
    <Modal
      title={modalTitle}
      open={isOpen}
      onCancel={handleModalCancel}
      footer={footer}
      destroyOnClose
      width={820}
      maskClosable={false}
      style={{ top: 32 }}
      styles={{
        footer: {
          marginTop: 0,
          paddingTop: 16,
          borderTop: `1px solid ${token.colorSplit}`,
        },
      }}
    >
      <Divider style={{ marginTop: '12px', marginBottom: '20px' }} />

      {isEditMode ? (
        <Tabs
          activeKey={String(current)}
          onChange={(key) => setCurrent(Number(key))}
          items={[
            {
              key: String(STEP_GENERAL),
              label: t('scenes.quizzes.scene.create.sections.general'),
            },
            {
              key: String(STEP_CONTENT),
              label: t('scenes.quizzes.scene.create.sections.content'),
            },
            {
              key: String(STEP_QUESTIONS),
              label: t('scenes.quizzes.scene.create.sections.questions'),
            },
          ]}
          style={{ marginBottom: 12 }}
        />
      ) : (
        <Steps
          current={current}
          size="small"
          items={[
            { title: t('scenes.quizzes.scene.create.sections.general') },
            { title: t('scenes.quizzes.scene.create.sections.content') },
            { title: t('scenes.quizzes.scene.create.sections.questions') },
          ]}
          style={{ marginBottom: 20 }}
        />
      )}

      <div>
        {loading ? (
          <div>
            <Skeleton.Input active block style={{ marginBottom: 16 }} />
            <Skeleton.Input active block style={{ marginBottom: 16 }} />
            <Skeleton.Input active block style={{ marginBottom: 16 }} />
            <Skeleton active title={false} paragraph={{ rows: 3 }} />
          </div>
        ) : (
          <Form
            form={form}
            layout="vertical"
            preserve={false}
            onValuesChange={(changed) => {
              if ('questions' in changed) setQuestionsTouched(true);
            }}
          >
            {/* PASO 1 — Datos generales */}
            <div
              style={{ display: current === STEP_GENERAL ? 'block' : 'none' }}
            >
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="countryCode"
                    label={t('scenes.quizzes.scene.create.labels.country')}
                    rules={[{ required: true }]}
                    tooltip={
                      isEditMode
                        ? t(
                            'scenes.quizzes.scene.create.country_locked_tooltip'
                          )
                        : undefined
                    }
                  >
                    <Select options={countryOptions} disabled={isEditMode} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="quizType"
                    label={t('scenes.quizzes.scene.create.labels.quiz_type')}
                    rules={[{ required: true }]}
                    tooltip={
                      isEditMode
                        ? t(
                            'scenes.quizzes.scene.create.quiz_type_locked_tooltip'
                          )
                        : undefined
                    }
                  >
                    <Select options={quizTypeOptions} disabled={isEditMode} />
                  </Form.Item>
                </Col>
                <Col xs={24}>
                  <Form.Item
                    name="name"
                    label={t('scenes.quizzes.scene.create.labels.name')}
                    rules={[
                      {
                        required: true,
                        message: t(
                          'scenes.quizzes.scene.create.validations.name_required'
                        ),
                      },
                    ]}
                  >
                    <Input
                      placeholder={t(
                        'scenes.quizzes.scene.create.placeholders.name'
                      )}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24}>
                  <Form.Item
                    name="description"
                    label={t('scenes.quizzes.scene.create.labels.description')}
                  >
                    <Input.TextArea
                      rows={2}
                      placeholder={t(
                        'scenes.quizzes.scene.create.placeholders.description'
                      )}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="questionsToShow"
                    label={t(
                      'scenes.quizzes.scene.create.labels.questions_to_show'
                    )}
                    style={{ marginBottom: 0 }}
                    rules={[
                      {
                        required: true,
                        type: 'integer',
                        min: 1,
                        message: t(
                          'scenes.quizzes.scene.create.validations.questions_to_show'
                        ),
                      },
                    ]}
                  >
                    <InputNumber min={1} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="passingScore"
                    label={t(
                      'scenes.quizzes.scene.create.labels.passing_score'
                    )}
                    dependencies={['questionsToShow']}
                    style={{ marginBottom: 0 }}
                    rules={[
                      {
                        required: true,
                        type: 'integer',
                        min: 1,
                        message: t(
                          'scenes.quizzes.scene.create.validations.passing_score'
                        ),
                      },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          const toShow = getFieldValue('questionsToShow');
                          if (value && toShow && value > toShow) {
                            return Promise.reject(
                              new Error(
                                t(
                                  'scenes.quizzes.scene.create.validations.passing_score'
                                )
                              )
                            );
                          }
                          return Promise.resolve();
                        },
                      }),
                    ]}
                  >
                    <InputNumber min={1} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>
            </div>

            {/* PASO 2 — Contenido del video */}
            <div
              style={{ display: current === STEP_CONTENT ? 'block' : 'none' }}
            >
              <Form.Item
                name="contentType"
                label={t('scenes.quizzes.scene.create.labels.content_type')}
                rules={[{ required: true }]}
              >
                <Segmented options={contentTypeSegments} />
              </Form.Item>

              {/* minHeight estabiliza la altura al alternar URL <-> subida */}
              <div style={{ minHeight: 150 }}>
                {contentType === 'touch_storage' ? (
                  <Form.Item
                    label={t('scenes.quizzes.scene.create.labels.video_file')}
                    required
                    style={{ marginBottom: 0 }}
                  >
                    <Dragger
                      accept="video/mp4"
                      maxCount={1}
                      multiple={false}
                      beforeUpload={beforeUpload}
                      fileList={uploadFileList}
                      disabled={uploading}
                      onRemove={() => {
                        if (uploadedFileUrl)
                          URL.revokeObjectURL(uploadedFileUrl);
                        setR2Key(null);
                        setUploadedFileName(null);
                        setUploadedFileUrl(null);
                        setUploadPercent(0);
                      }}
                    >
                      <p className="ant-upload-drag-icon">
                        <InboxOutlined />
                      </p>
                      <p className="ant-upload-text">
                        {t('scenes.quizzes.scene.create.upload_drag_text')}
                      </p>
                      {maxVideoSizeMb && (
                        <p className="ant-upload-hint">
                          {t('scenes.quizzes.scene.create.hints.video_max', {
                            max: maxVideoSizeMb,
                          })}
                        </p>
                      )}
                    </Dragger>
                  </Form.Item>
                ) : (
                  <Form.Item
                    name="contentUrl"
                    label={t('scenes.quizzes.scene.create.labels.content_url')}
                    style={{ marginBottom: 0 }}
                    rules={[
                      {
                        required: true,
                        message: t(
                          'scenes.quizzes.scene.create.validations.content_url_required'
                        ),
                      },
                      {
                        type: 'url',
                        message: t(
                          'scenes.quizzes.scene.create.validations.content_url_invalid'
                        ),
                      },
                    ]}
                  >
                    <Input.TextArea
                      rows={3}
                      placeholder={t(
                        'scenes.quizzes.scene.create.placeholders.content_url'
                      )}
                    />
                  </Form.Item>
                )}
              </div>
            </div>

            {/* PASO 3 — Banco de preguntas */}
            <div
              style={{ display: current === STEP_QUESTIONS ? 'block' : 'none' }}
            >
              {missingQuestions > 0 && (
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginBottom: 16 }}
                  message={t(
                    'scenes.quizzes.scene.create.validations.questions_insufficient',
                    {
                      toShow: watchedToShow,
                      count: questionCount,
                      missing: missingQuestions,
                    }
                  )}
                />
              )}

              <Form.List name="questions">
                {(
                  questionFields,
                  { add: addQuestion, remove: removeQuestion }
                ) => (
                  <>
                    {questionFields.map((questionField, qIdx) => (
                      <div
                        key={questionField.key}
                        style={{
                          border: `1px solid ${token.colorBorderSecondary}`,
                          borderRadius: token.borderRadiusLG,
                          background: token.colorFillQuaternary,
                          padding: 16,
                          marginBottom: 12,
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            marginBottom: 12,
                          }}
                        >
                          <span
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 8,
                              fontWeight: 600,
                            }}
                          >
                            <span
                              style={{
                                width: 24,
                                height: 24,
                                borderRadius: 6,
                                display: 'inline-grid',
                                placeItems: 'center',
                                background: token.colorPrimaryBg,
                                color: token.colorPrimary,
                                fontSize: 13,
                              }}
                            >
                              {qIdx + 1}
                            </span>
                            {t(
                              'scenes.quizzes.scene.create.labels.question_text'
                            )}
                          </span>
                          <Tooltip
                            title={t(
                              'scenes.quizzes.scene.create.buttons.remove_question'
                            )}
                          >
                            <Button
                              type="text"
                              danger
                              icon={<DeleteOutlined />}
                              disabled={questionFields.length <= 1}
                              onClick={() => {
                                removeQuestion(questionField.name);
                                setQuestionsTouched(true);
                              }}
                            />
                          </Tooltip>
                        </div>

                        <Form.Item
                          name={[questionField.name, 'text']}
                          rules={[
                            {
                              required: true,
                              message: t(
                                'scenes.quizzes.scene.create.validations.question_text_required'
                              ),
                            },
                          ]}
                        >
                          <Input
                            placeholder={t(
                              'scenes.quizzes.scene.create.placeholders.question_text'
                            )}
                          />
                        </Form.Item>

                        <div
                          style={{
                            marginBottom: 8,
                            color: token.colorTextSecondary,
                            fontSize: 13,
                          }}
                        >
                          {t('scenes.quizzes.scene.create.options_hint')}
                        </div>

                        <Form.Item
                          name={[questionField.name, 'correctIndex']}
                          noStyle
                        >
                          <Radio.Group style={{ width: '100%' }}>
                            <Form.List name={[questionField.name, 'options']}>
                              {(
                                optionFields,
                                { add: addOption, remove: removeOption }
                              ) => (
                                <>
                                  {optionFields.map((optionField, oIdx) => (
                                    <Row
                                      key={optionField.key}
                                      gutter={8}
                                      align="middle"
                                      style={{ marginBottom: 8 }}
                                    >
                                      <Col flex="none">
                                        <Radio value={oIdx} />
                                      </Col>
                                      <Col flex="auto">
                                        <Form.Item
                                          name={[optionField.name, 'text']}
                                          noStyle
                                          rules={[
                                            {
                                              required: true,
                                              message: t(
                                                'scenes.quizzes.scene.create.validations.option_text_required'
                                              ),
                                            },
                                          ]}
                                        >
                                          <Input
                                            placeholder={t(
                                              'scenes.quizzes.scene.create.placeholders.option_text',
                                              { index: oIdx + 1 }
                                            )}
                                          />
                                        </Form.Item>
                                      </Col>
                                      <Col flex="none">
                                        <Tooltip
                                          title={t(
                                            'scenes.quizzes.scene.create.buttons.remove_option'
                                          )}
                                        >
                                          <Button
                                            type="text"
                                            danger
                                            icon={<DeleteOutlined />}
                                            disabled={optionFields.length <= 2}
                                            onClick={() => {
                                              removeOption(optionField.name);
                                              setQuestionsTouched(true);
                                            }}
                                          />
                                        </Tooltip>
                                      </Col>
                                    </Row>
                                  ))}
                                  <Button
                                    type="dashed"
                                    icon={<PlusOutlined />}
                                    onClick={() => {
                                      addOption({ text: '' });
                                      setQuestionsTouched(true);
                                    }}
                                  >
                                    {t(
                                      'scenes.quizzes.scene.create.buttons.add_option'
                                    )}
                                  </Button>
                                </>
                              )}
                            </Form.List>
                          </Radio.Group>
                        </Form.Item>
                      </div>
                    ))}
                    <Button
                      type="dashed"
                      block
                      icon={<PlusOutlined />}
                      onClick={() => {
                        addQuestion({
                          text: '',
                          active: true,
                          correctIndex: 0,
                          options: [{ text: '' }, { text: '' }],
                        });
                        setQuestionsTouched(true);
                      }}
                    >
                      {t('scenes.quizzes.scene.create.buttons.add_question')}
                    </Button>
                  </>
                )}
              </Form.List>
            </div>
          </Form>
        )}
      </div>
    </Modal>
  );
}

export default CreateQuizModal;
